import { useState } from "react";
import { useNavigate } from "react-router";

import { ApiError, fieldErrors } from "../api/client";
import type { Impact, ResolutionCode, TicketDetail, TicketType, Urgency } from "../api/types";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { TextAreaField, TextField, ToggleRow } from "../ui/Field";
import { Notice, errorMessage } from "../ui/Notice";
import { Select } from "../ui/Select";
import { useToast } from "../ui/Toast";
import { useCatalogue, useTicketAction } from "./api";
import { AssignTarget, startingTarget, targetProblem, useAssignBody, type AssignBody, type AssignTargetValue } from "./AssignTarget";
import {
  IMPACT_DETAILS,
  IMPACT_LABELS,
  PRIORITY_LABELS,
  RESOLUTION_LABELS,
  TYPE_LABELS,
  URGENCY_DETAILS,
  URGENCY_DETAILS_REQUEST,
  URGENCY_LABELS,
  resolutionCodesFor,
  suggestedPriority,
} from "./labels";

interface TicketDialogProps {
  ticket: TicketDetail;
  onClose: () => void;
  onReload: () => void;
}

/** A failed change. A 409 means someone else changed the ticket: offer to reload it. */
export function ChangeError({ error, onReload }: { error: unknown; onReload: () => void }) {
  if (!error || (error instanceof ApiError && error.status === 422 && fieldErrors(error).length > 0)) return null;
  const conflict = error instanceof ApiError && error.status === 409;
  return (
    <Notice tone="error">
      {errorMessage(error)}{" "}
      {conflict ? (
        <button type="button" className="link-btn" onClick={onReload}>
          Reload ticket
        </button>
      ) : null}
    </Notice>
  );
}

function footer(onClose: () => void, label: string, pending: boolean, variant: "primary" | "danger" = "primary") {
  return (
    <>
      <Button onClick={onClose}>Cancel</Button>
      <Button type="submit" variant={variant} loading={pending}>
        {label}
      </Button>
    </>
  );
}

export function AssignDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const [target, setTarget] = useState<AssignTargetValue>(() => startingTarget(ticket));
  const [reason, setReason] = useState("");
  const [showProblem, setShowProblem] = useState(false);
  const { toBody, creating, createError } = useAssignBody();
  const assign = useTicketAction<AssignBody & { reason: string }>(ticket.reference, "assign");
  // Taking it from the person working it is a transfer: the reason goes on its history.
  const transfer = ticket.owner !== null && !(target.kind === "person" && target.person?.user_id === ticket.owner.id);
  const reasonError = fieldErrors(assign.error).find((e) => e.field === "reason")?.message;
  const problem = targetProblem(target);

  async function submit() {
    setShowProblem(true);
    if (problem) return;
    try {
      const body = await toBody(target);
      assign.mutate(
        { version: ticket.version, ...body, reason },
        {
          onSuccess: (updated) => {
            onClose();
            // Passed on to someone else, and no longer yours to see.
            if (!updated) {
              toast.success(`${ticket.reference} passed on.`);
              navigate("/tickets");
            }
          },
        },
      );
    } catch {
      // The new group couldn't be made: createError says why.
    }
  }

  return (
    <Dialog
      open
      title={`Assign ${ticket.reference}`}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={footer(onClose, "Assign", assign.isPending || creating)}
    >
      <AssignTarget value={target} onChange={setTarget} problem={showProblem ? problem : null} />
      <TextAreaField
        label={transfer ? "Reason for the transfer" : "Note (optional)"}
        hint={transfer ? "Required: it's recorded on the ticket's history." : undefined}
        value={reason}
        maxLength={1000}
        rows={2}
        error={reasonError}
        onChange={(event) => setReason(event.target.value)}
      />
      {createError ? <Notice tone="error">{errorMessage(createError)}</Notice> : null}
      <ChangeError error={assign.error} onReload={onReload} />
    </Dialog>
  );
}

export function ResolveDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const codes = resolutionCodesFor(ticket.type);
  const [code, setCode] = useState<ResolutionCode>(codes[0] ?? "fixed");
  const [cause, setCause] = useState("");
  const [outcome, setOutcome] = useState("");
  const [verification, setVerification] = useState("");
  // Suggest the fix to people reporting the same thing (DECISIONS #52); never for restricted tickets.
  const [shareFix, setShareFix] = useState(!ticket.is_restricted);
  const resolve = useTicketAction<{
    code: ResolutionCode;
    cause: string;
    outcome: string;
    verification: string;
    share_fix: boolean;
  }>(ticket.reference, "resolve");
  const errors = fieldErrors(resolve.error);
  const error = (field: string) => errors.find((e) => e.field === field)?.message;
  const incident = ticket.type === "incident";

  return (
    <Dialog
      open
      wide
      title={`Resolve ${ticket.reference}`}
      onClose={onClose}
      onSubmit={() =>
        resolve.mutate(
          { version: ticket.version, code, cause, outcome, verification, share_fix: shareFix && !ticket.is_restricted },
          { onSuccess: onClose },
        )
      }
      footer={footer(onClose, "Resolve ticket", resolve.isPending)}
    >
      <p className="muted">
        The requester sees what was done and is asked to confirm.
      </p>
      <div className="field">
        <label className="field-label" htmlFor="resolve-code">
          Resolution
        </label>
        <Select
          id="resolve-code"
          label="Resolution"
          value={code}
          onChange={(value) => setCode(value as ResolutionCode)}
          groups={[{ options: codes.map((c) => ({ value: c, label: RESOLUTION_LABELS[c] })) }]}
        />
      </div>
      {incident ? (
        <TextField
          label="Cause"
          hint="The confirmed cause, or 'Not determined'."
          value={cause}
          maxLength={5000}
          error={error("cause")}
          onChange={(event) => setCause(event.target.value)}
        />
      ) : null}
      <TextAreaField
        label={incident ? "What was done" : "How it was fulfilled"}
        value={outcome}
        rows={4}
        maxLength={5000}
        error={error("outcome")}
        onChange={(event) => setOutcome(event.target.value)}
      />
      <TextField
        label="How it was confirmed"
        hint="e.g. 'Requester confirmed on a call'."
        value={verification}
        maxLength={5000}
        error={error("verification")}
        onChange={(event) => setVerification(event.target.value)}
      />
      {ticket.is_restricted ? null : (
        <div className="toggle-list">
          <ToggleRow
            label="Suggest this fix to others"
            description="When someone reports something similar, they see the title and fix, without names."
            checked={shareFix}
            onChange={setShareFix}
          />
        </div>
      )}
      <ChangeError error={resolve.error} onReload={onReload} />
    </Dialog>
  );
}

export function ReopenDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const [reason, setReason] = useState("");
  const reopen = useTicketAction<{ reason: string }>(ticket.reference, "reopen");
  return (
    <Dialog
      open
      title={`Reopen ${ticket.reference}?`}
      onClose={onClose}
      onSubmit={() => reopen.mutate({ version: ticket.version, reason }, { onSuccess: onClose })}
      footer={footer(onClose, "Reopen", reopen.isPending)}
    >
      <TextAreaField
        label="What's still wrong?"
        value={reason}
        rows={3}
        maxLength={2000}
        error={fieldErrors(reopen.error).find((e) => e.field === "reason")?.message}
        onChange={(event) => setReason(event.target.value)}
      />
      <ChangeError error={reopen.error} onReload={onReload} />
    </Dialog>
  );
}

export function PriorityDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const [impact, setImpact] = useState<Impact>(ticket.impact);
  const [urgency, setUrgency] = useState<Urgency>(ticket.urgency);
  const [override, setOverride] = useState(ticket.priority !== ticket.suggested_priority);
  const [priority, setPriority] = useState(ticket.priority);
  const [reason, setReason] = useState(ticket.priority_reason ?? "");
  const change = useTicketAction<{ impact: Impact; urgency: Urgency; priority: number | null; reason: string }>(
    ticket.reference,
    "priority",
  );
  const suggested = suggestedPriority(impact, urgency);

  return (
    <Dialog
      open
      title={`Priority for ${ticket.reference}`}
      onClose={onClose}
      onSubmit={() =>
        change.mutate(
          { version: ticket.version, impact, urgency, priority: override ? priority : null, reason: override ? reason : "" },
          { onSuccess: onClose },
        )
      }
      footer={footer(onClose, "Save priority", change.isPending)}
    >
      <div className="form-grid">
        <div className="field">
          <label className="field-label" htmlFor="impact">
            Impact
          </label>
          <Select
            id="impact"
            label="Impact"
            value={impact}
            onChange={(value) => setImpact(value as Impact)}
            groups={[
              {
                options: (Object.keys(IMPACT_LABELS) as Impact[]).map((value) => ({
                  value,
                  label: IMPACT_LABELS[value],
                  detail: IMPACT_DETAILS[value],
                })),
              },
            ]}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="urgency">
            Urgency
          </label>
          <Select
            id="urgency"
            label="Urgency"
            value={urgency}
            onChange={(value) => setUrgency(value as Urgency)}
            groups={[
              {
                options: (Object.keys(URGENCY_LABELS) as Urgency[]).map((value) => ({
                  value,
                  label: URGENCY_LABELS[value],
                  detail: (ticket.type === "service_request" ? URGENCY_DETAILS_REQUEST : URGENCY_DETAILS)[value],
                })),
              },
            ]}
          />
        </div>
      </div>
      <Notice tone="info">Suggested priority: {PRIORITY_LABELS[suggested]}</Notice>
      {ticket.can.override_priority ? (
        <>
          <label className="choice">
            <input type="checkbox" checked={override} onChange={(event) => setOverride(event.target.checked)} />
            <span>Override the suggested priority</span>
          </label>
          {override ? (
            <>
              <div className="field">
                <label className="field-label" htmlFor="priority">
                  Priority
                </label>
                <Select
                  id="priority"
                  label="Priority"
                  value={String(priority)}
                  onChange={(value) => setPriority(Number(value))}
                  groups={[{ options: [1, 2, 3, 4].map((p) => ({ value: String(p), label: PRIORITY_LABELS[p] ?? `P${p}` })) }]}
                />
              </div>
              <TextAreaField
                label="Reason for the override"
                hint="Required, and recorded in the audit log."
                value={reason}
                rows={2}
                maxLength={1000}
                error={fieldErrors(change.error).find((e) => e.field === "reason")?.message}
                onChange={(event) => setReason(event.target.value)}
              />
            </>
          ) : null}
        </>
      ) : null}
      <ChangeError error={change.error} onReload={onReload} />
    </Dialog>
  );
}

export function ClassifyDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const catalogue = useCatalogue();
  const [type, setType] = useState<TicketType>(ticket.type);
  const [serviceId, setServiceId] = useState(ticket.service?.id ?? "");
  const [categoryId, setCategoryId] = useState(ticket.category?.id ?? "");
  const change = useTicketAction<{ type: TicketType; service_id: string | null; category_id: string | null }>(
    ticket.reference,
    "classification",
  );
  const service = catalogue.data?.find((s) => s.id === serviceId);
  const categories = service?.categories.flatMap((c) => [
    { id: c.id, name: c.name },
    ...c.subcategories.map((s) => ({ id: s.id, name: `${c.name} › ${s.name}` })),
  ]);

  return (
    <Dialog
      open
      title={`Service for ${ticket.reference}`}
      onClose={onClose}
      onSubmit={() =>
        change.mutate(
          { version: ticket.version, type, service_id: serviceId || null, category_id: categoryId || null },
          { onSuccess: onClose },
        )
      }
      footer={footer(onClose, "Save", change.isPending)}
    >
      <p className="muted">Route by root cause. If nobody has it yet, it moves to the new service's group.</p>
      <div className="field">
        <label className="field-label" htmlFor="classify-type">
          Type
        </label>
        <Select
          id="classify-type"
          label="Type"
          value={type}
          onChange={(value) => setType(value as TicketType)}
          groups={[{ options: (Object.keys(TYPE_LABELS) as TicketType[]).map((value) => ({ value, label: TYPE_LABELS[value] })) }]}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="classify-service">
          Service
        </label>
        <Select
          id="classify-service"
          label="Service"
          value={serviceId}
          onChange={(value) => {
            setServiceId(value);
            setCategoryId("");
          }}
          groups={[
            { options: [{ value: "", label: "Not sure" }] },
            { options: (catalogue.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
          ]}
        />
      </div>
      {categories && categories.length > 0 ? (
        <div className="field">
          <label className="field-label" htmlFor="classify-category">
            Category
          </label>
          <Select
            id="classify-category"
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            groups={[
              { options: [{ value: "", label: "None" }] },
              { options: categories.map((c) => ({ value: c.id, label: c.name })) },
            ]}
          />
        </div>
      ) : null}
      <ChangeError error={change.error} onReload={onReload} />
    </Dialog>
  );
}

export function DuplicateDialog({ ticket, onClose, onReload }: TicketDialogProps) {
  const [original, setOriginal] = useState("");
  const mark = useTicketAction<{ original: string }>(ticket.reference, "duplicate");
  return (
    <Dialog
      open
      title={`Mark ${ticket.reference} as a duplicate`}
      onClose={onClose}
      onSubmit={() => mark.mutate({ version: ticket.version, original: original.trim() }, { onSuccess: onClose })}
      footer={footer(onClose, "Mark as duplicate", mark.isPending)}
    >
      <p className="muted">
        Resolves this ticket and links it to the original. It can be reopened later.
      </p>
      <TextField
        label="Original ticket"
        placeholder="ASP-000123"
        value={original}
        maxLength={20}
        onChange={(event) => setOriginal(event.target.value)}
      />
      <ChangeError error={mark.error} onReload={onReload} />
    </Dialog>
  );
}
